// Imports
import { faArrowRightToBracket, faPlus } from '@fortawesome/pro-regular-svg-icons';
import React, { useCallback, useContext, useEffect } from 'react';
import Box from '../../../shareables/foundation/admin-ui/components/content/box';
import Heading from '../../../shareables/foundation/admin-ui/components/content/heading';
import PageAction from '../../../shareables/foundation/admin-ui/components/content/page-action';
import PageActions from '../../../shareables/foundation/admin-ui/components/content/page-actions';
import Table from '../../../shareables/foundation/admin-ui/components/content/table';
import LoadingRow from '../../../shareables/foundation/front-end/components/tables/loading-row';
import { useAppDispatch } from '../../../shareables/foundation/front-end/redux/hooks';
import useCachedState from '../../../shareables/foundation/front-end/utils/use-cached-state';
import apiActions from '../../../shareables/integrations/api/redux/actions';
import { GetWebsitesResponse } from '../../../shareables/types/api/main/reseller/websites/get';
import ResellerContext from '../../resources/reseller-provider/context';
import WebsitesRow from './row';


// Define the accepted props
interface Props {
	storybookWebsites?: GetWebsitesResponse;
}


// Function component
const Websites: React.FC<Props> = ({ storybookWebsites }) => {
	// Use state
	const [websites, setWebsites] = useCachedState<GetWebsitesResponse | null>(storybookWebsites ?? null, 'websites');
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use context
	const reseller = useContext(ResellerContext);
	
	
	// Function that loads websites
	const memoizedLoadWebsites = useCallback(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetWebsitesResponse>({
				action: 'GET',
				uri: `/reseller/${reseller?.id ?? ''}/websites`,
				completion: (json) => {
					setWebsites(json.response);
				},
			})
		);
	}, [reseller?.id, dispatch, setWebsites]);
	
	
	// Load websites on mount
	useEffect(() => {
		memoizedLoadWebsites();
	}, [memoizedLoadWebsites]);
	
	
	// Return JSX
	return (
		<React.Fragment>
			<Heading size={1}>Websites</Heading>
			
			<PageActions>
				<PageAction icon={faPlus} text='Create new website' to='create' tooltip='Create a new community website' />
				<PageAction
					icon={faArrowRightToBracket}
					text='Adopt existing website'
					to='adopt'
					tooltip='Adopt an existing community website into your reseller account'
				/>
			</PageActions>
			
			<Box>
				<Table>
					<thead>
						<tr>
							<th>Community</th>
							<th>Price</th>
							<th>Renews</th>
							<th style={{ textAlign: 'center' }}>Plan</th>
							<th style={{ textAlign: 'center' }}>Size</th>
							<th style={{ textAlign: 'center' }}>Remove</th>
						</tr>
					</thead>
					
					<tbody>
						{!websites && <LoadingRow />}
						
						{websites?.length === 0 && (
							<tr>
								<td colSpan={999} style={{ textAlign: 'center' }}>
									<em>No websites yet…</em>
								</td>
							</tr>
						)}
						
						{websites?.map((website) => (
							<WebsitesRow key={website.id} website={website} onChange={memoizedLoadWebsites} />
						))}
					</tbody>
				</Table>
			</Box>
		</React.Fragment>
	);
};

export default Websites;
